import React from 'react';
import { Link } from 'react-router-dom';
import { useProducts, useRecommendations } from '../api';
import { ProductGrid } from '../components/product/ProductGrid';
import { HeroCarousel } from '../components/hero';
import { useFeatureEnabled, useSEOSettings } from '../contexts/SettingsContext';
import { SEO } from '../components/SEO';
import { useScrollReveal, useCountUp } from '../hooks/useScrollReveal';
import { UI_CONSTANTS } from '@petmas/shared';

// Categories with custom SVG icons styled in duotone
const categories = [
  {
    slug: 'perros',
    label: 'Perros',
    icon: DogIcon,
    colorClass: 'bg-primary/10 text-primary',
  },
  {
    slug: 'gatos',
    label: 'Gatos',
    icon: CatIcon,
    colorClass: 'bg-secondary/10 text-secondary',
  },
  {
    slug: 'prendas',
    label: 'Prendas',
    icon: ShirtIcon,
    colorClass: 'bg-primary/10 text-primary',
  },
  {
    slug: 'placas',
    label: 'Placas',
    icon: TagIcon,
    colorClass: 'bg-secondary/10 text-secondary',
  },
  {
    slug: 'farmacia',
    label: 'Farmacia',
    icon: MedicineIcon,
    colorClass: 'bg-primary/10 text-primary',
  },
  {
    slug: 'outlet',
    label: 'Outlet',
    icon: SaleIcon,
    colorClass: 'bg-accent/10 text-accent',
  },
];

const features = [
  {
    icon: TruckIcon,
    title: 'Envio Gratis',
    description: 'En pedidos mayores a S/150',
  },
  {
    icon: ShieldIcon,
    title: 'Pago Seguro',
    description: 'Todas las tarjetas y Yape',
  },
  {
    icon: RefreshIcon,
    title: 'Devoluciones',
    description: '30 dias para cambios',
  },
  {
    icon: ChatIcon,
    title: 'Asistente IA',
    description: 'Te ayudamos a elegir',
  },
];

export function HomePage() {
  const { data: productsData, isLoading: isLoadingProducts } = useProducts({ pageSize: UI_CONSTANTS.FEATURED_PRODUCTS_LIMIT });
  const { data: recommendations, isLoading: isLoadingRecommendations } = useRecommendations();
  const newsletterEnabled = useFeatureEnabled('newsletterEnabled');
  const seoSettings = useSEOSettings();

  // Scroll reveal hooks
  const categoriesReveal = useScrollReveal();
  const featuredReveal = useScrollReveal();
  const recommendationsReveal = useScrollReveal();
  const trustReveal = useScrollReveal();

  // Count up animations for stats
  const clientsCount = useCountUp({ end: UI_CONSTANTS.COUNT_UP_DURATION_MS, duration: UI_CONSTANTS.COUNT_UP_LONG_DURATION_MS });
  const productsCount = useCountUp({ end: UI_CONSTANTS.DEBOUNCE_DELAY_MS, duration: UI_CONSTANTS.COUNT_UP_ALT_DURATION_MS });

  // Build canonical URL for home page
  const canonicalUrl = typeof window !== 'undefined' ? window.location.origin : undefined;

  return (
    <div className="bg-warm-white">
      {/* SEO - Home page uses site name and description from settings */}
      <SEO
        description={seoSettings.siteDescription}
        canonical={canonicalUrl}
        ogImage={seoSettings.defaultOgImage}
      />

      {/* Hero Carousel */}
      <HeroCarousel />

      {/* Features Bar */}
      <section className="border-b border-warm-gray/10 bg-cream">
        <div className="container py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className="animate-entrance flex items-center gap-3 p-3 rounded-xl hover:bg-white transition-colors"
                style={{ animationDelay: `${index * UI_CONSTANTS.STAGGER_DELAY_MS}ms` }}
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                  <feature.icon className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-semibold text-secondary">{feature.title}</p>
                  <p className="text-sm text-gray-500">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section
        ref={categoriesReveal.ref}
        className={`py-16 scroll-reveal ${categoriesReveal.isRevealed ? 'revealed' : ''}`}
      >
        <div className="container">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-display font-bold text-secondary">
              Compra por Categoria
            </h2>
            <p className="mt-2 text-gray-500">
              Todo lo que tu mascota necesita en un solo lugar
            </p>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4 md:gap-6">
            {categories.map((category, index) => (
              <Link
                key={category.slug}
                to={`/${category.slug}`}
                className="animate-entrance flex flex-col items-center p-4 rounded-2xl hover:bg-cream transition-all group"
                style={{ animationDelay: `${index * UI_CONSTANTS.STAGGER_DELAY_MS}ms` }}
              >
                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${category.colorClass}`}>
                  <category.icon className="w-8 h-8" />
                </div>
                <span className="mt-3 text-sm font-semibold text-secondary group-hover:text-primary transition-colors">
                  {category.label}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section
        ref={featuredReveal.ref}
        className={`py-16 bg-cream scroll-reveal ${featuredReveal.isRevealed ? 'revealed' : ''}`}
      >
        <div className="container">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="text-3xl font-display font-bold text-secondary">
                Productos Destacados
              </h2>
              <p className="mt-2 text-gray-500">
                Los favoritos de nuestros clientes
              </p>
            </div>
            <Link
              to="/buscar"
              className="hidden sm:inline-flex items-center gap-2 text-primary hover:text-primary-dark font-semibold"
            >
              Ver todos
              <ArrowRightIcon className="w-4 h-4" />
            </Link>
          </div>
          <ProductGrid
            products={productsData?.items ?? []}
            isLoading={isLoadingProducts}
          />
          <div className="mt-8 text-center sm:hidden">
            <Link to="/buscar" className="btn btn-outline">
              Ver todos los productos
            </Link>
          </div>
        </div>
      </section>

      {/* AI Recommendations */}
      {recommendations && recommendations.length > 0 && (
        <section
          ref={recommendationsReveal.ref}
          className={`py-16 scroll-reveal ${recommendationsReveal.isRevealed ? 'revealed' : ''}`}
        >
          <div className="container">
            <div className="flex items-end justify-between mb-8">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-3">
                  <SparkleIcon className="w-4 h-4" />
                  Recomendado por IA
                </div>
                <h2 className="text-3xl font-display font-bold text-secondary">
                  Para Ti
                </h2>
                <p className="mt-2 text-gray-500">
                  Basado en tus preferencias y compras anteriores
                </p>
              </div>
            </div>
            <ProductGrid
              products={recommendations}
              isLoading={isLoadingRecommendations}
            />
          </div>
        </section>
      )}

      {/* Newsletter / CTA */}
      {newsletterEnabled && (
        <section className="py-16 bg-tan/30">
          <div className="container">
            <div className="max-w-2xl mx-auto text-center relative z-10">
              <h2 className="text-3xl font-display font-bold text-secondary">
                Recibe ofertas exclusivas
              </h2>
              <p className="mt-3 text-gray-600">
                Suscribete a nuestro boletin y recibe un{' '}
                <span className="font-bold text-primary">10% de descuento</span>{' '}
                en tu primera compra
              </p>
              <form className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="Tu correo electronico"
                  className="input flex-1"
                />
                <button type="submit" className="btn btn-primary whitespace-nowrap">
                  Suscribirse
                </button>
              </form>
              <p className="mt-4 text-xs text-gray-500">
                Sin spam, solo las mejores ofertas para tu mascota
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Trust badges with count up */}
      <section
        ref={trustReveal.ref}
        className={`py-12 border-t border-gray-200 scroll-reveal ${trustReveal.isRevealed ? 'revealed' : ''}`}
      >
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            <TrustBadge
              icon={ShieldCheckIcon}
              title="Compra Segura"
              description="Proteccion de datos"
            />
            <TrustBadge
              icon={TruckFastIcon}
              title="Envio Rapido"
              description="24-48h Lima"
            />
            <div className="flex items-center gap-4 p-4 rounded-xl">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                <StarIcon className="w-8 h-8" />
              </div>
              <div>
                <p className="font-display text-2xl font-bold text-secondary">
                  +<span ref={clientsCount.ref} className="count-up">{clientsCount.value}</span>
                </p>
                <p className="text-sm text-gray-500">Clientes Felices</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 rounded-xl">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                <BoxIcon className="w-8 h-8" />
              </div>
              <div>
                <p className="font-display text-2xl font-bold text-secondary">
                  +<span ref={productsCount.ref} className="count-up">{productsCount.value}</span>
                </p>
                <p className="text-sm text-gray-500">Productos</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function TrustBadge({
  icon: Icon,
  title,
  description,
}: {
  icon: React.FC<{ className?: string }>;
  title: string;
  description: string;
}) {
  return (
    <div className="flex items-center gap-4 p-4 rounded-xl">
      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
        <Icon className="w-8 h-8" />
      </div>
      <div>
        <p className="font-semibold text-secondary">{title}</p>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
    </div>
  );
}

// Category Icons (Duotone style)
function DogIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.5c-3.5 0-6 2.5-6 6v1.5c0 1.5-1.5 3-1.5 4.5s1.5 3 3 3h1.5M12 4.5c3.5 0 6 2.5 6 6v1.5c0 1.5 1.5 3 1.5 4.5s-1.5 3-3 3h-1.5M12 4.5V3M9 19.5v1.5M15 19.5v1.5M10.5 12a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM16.5 12a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
    </svg>
  );
}

function CatIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 20c-4 0-7-3-7-7v-3l-2-4h4l1 2h8l1-2h4l-2 4v3c0 4-3 7-7 7zM9.5 13a1 1 0 11-2 0 1 1 0 012 0zM16.5 13a1 1 0 11-2 0 1 1 0 012 0zM12 16c1 0 2-.5 2-1.5S13 13 12 13s-2 .5-2 1.5.5 1.5 2 1.5z" />
    </svg>
  );
}

function ShirtIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6.5 3L3 6.5V20a1 1 0 001 1h16a1 1 0 001-1V6.5L17.5 3m-11 0L9 5.5h6l2.5-2.5m-11 0h11M3 6.5h18" />
    </svg>
  );
}

function TagIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
    </svg>
  );
}

function MedicineIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.5 12c0 1.232-.046 2.453-.138 3.662a4.006 4.006 0 01-3.7 3.7 48.678 48.678 0 01-7.324 0 4.006 4.006 0 01-3.7-3.7c-.017-.22-.032-.441-.046-.662M19.5 12l3-3m-3 3l-3-3m-12 3c0 1.232.046 2.453.138 3.662a4.006 4.006 0 003.7 3.7 48.657 48.657 0 007.324 0 4.006 4.006 0 003.7-3.7c.017-.22.032-.441.046-.662M4.5 12l3 3m-3-3l-3 3M12 3v6m0-6l3 3m-3-3L9 6" />
    </svg>
  );
}

function SaleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 6h.008v.008H6V6z" />
    </svg>
  );
}

// Feature Icons
function TruckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
    </svg>
  );
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
    </svg>
  );
}

function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
    </svg>
  );
}

function ChatIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
    </svg>
  );
}

function ArrowRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
    </svg>
  );
}

function SparkleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
    </svg>
  );
}

// Trust Badge Icons
function ShieldCheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
    </svg>
  );
}

function TruckFastIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
    </svg>
  );
}

function StarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
    </svg>
  );
}

function BoxIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" />
    </svg>
  );
}
